<script setup lang="ts">
import SvgIcon from '@/components/base/SvgIcon.vue';
import type { RuleEnum } from '@/constants/enums/RuleEnum';
import { useValidator } from '@/composables/useValidator';
import { computed, ref } from 'vue';

const props = defineProps<{
  inputTitle: string;
  inputType: string;
  inputPlaceHolder?: string;
  required?: boolean;
  canShowInputValue?: boolean;
  verifyRule?: RuleEnum;
}>();

const inputValue = defineModel<string>('inputValue', { required: true });
const { errorMessage, handleValidate } = useValidator();
const type = ref(props.inputType);
const showInput = ref(props.canShowInputValue);

const canDeleteAll = computed(() => {
  if (inputValue.value.trim() === '') {
    return false;
  } else {
    return true;
  }
});

const cleanInputValue = () => {
  inputValue.value = '';
};

const changeMode = (showInput: boolean): { showInput: boolean; type: string } => ({
  showInput: !showInput,
  type: showInput ? 'password' : 'text'
});

const correctFormat = ref(true);

const handleBlur = () => {
  if (props.verifyRule) {
    correctFormat.value = handleValidate(props.verifyRule, inputValue.value);
  }
  return correctFormat.value
};

defineExpose({ handleBlur });
</script>

<template>
  <div class="card">
    <div class="for-slot">
      <div class="input-title-block">
        <p class="input-title">{{ props.inputTitle }}</p>
        <p class="require-star" v-if="props.required">*</p>
      </div>
      <div class="input-value-block">
        <div class="for-slot">
          <input
            :class="!correctFormat ? 'incorrect-block' : ''"
            v-model="inputValue"
            :placeholder="props.inputPlaceHolder"
            :type="type"
            @blur="handleBlur"
          />
          <div class="inside-input-btn">
            <SvgIcon
              @click="cleanInputValue"
              class="cursor-pointer"
              color="grey"
              name="cross"
              v-if="canDeleteAll"
            />
            <SvgIcon
              class="cursor-pointer"
              color="grey"
              @click="
                showInput = changeMode(showInput)['showInput'];
                type = changeMode(showInput)['type'];
              "
              v-if="props.canShowInputValue"
              :name="showInput ? 'eyeOff' : 'eye'"
            />
          </div>
        </div>
        <slot />
      </div>
      <div v-if="!correctFormat">
        <p class="uncorrect-hint">{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
  .input-title-block {
    display: flex;
    font-weight: 600;
    margin-bottom: 4px;
    .input-title {
      color: #19191999;
    }
    .require-star {
      color: #ea4425;
    }
  }
  .input-value-block {
    position: relative;
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    .for-slot {
      position: relative;
      flex: 1;
    }

    input {
      width: 100%;
      border: 1px solid #0000001a;
      border-radius: 4px;
      overflow: hidden;
      padding: 0 12px;
      height: 48px;
      font-size: 16px;
      color: #191919cc;
      &.incorrect-block {
        border: 1px solid #ea4425;
        border-radius: 4px;
      }
      &:focus {
        outline: 1px solid $color-grey-60;
      }
    }
    .inside-input-btn {
      display: flex;
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  .uncorrect-hint {
    font-size: 14px;
    margin-top: 4px;
    color: #ea4425;
  }
}
</style>
